import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, createIdempotencyKey, isEndpointUnavailable } from "../api/client";
import type { TaskDetail } from "../api/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { StatusPill } from "../components/StatusPill";
import { useTaskFeed } from "../context/TaskFeedContext";
import { useToast } from "../context/ToastContext";
import { formatDate, statusLabel, taskIsActive, taskIsDraining, taskProgress } from "../utils/format";

const RETRYABLE_ITEM_STATUSES = new Set(["failed", "skipped_insufficient_credits"]);

function isRetryableItem(item: TaskDetail["items"][number]) {
  return Boolean(item.item_id) && RETRYABLE_ITEM_STATUSES.has(item.status);
}

export function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const feed = useTaskFeed();
  const { pushToast } = useToast();
  const id = Number(taskId);
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unsupported, setUnsupported] = useState(false);
  const [revision, setRevision] = useState(0);
  const [selectedFailedIds, setSelectedFailedIds] = useState<number[]>([]);
  const [confirmAction, setConfirmAction] = useState<"cancel" | "retry" | null>(null);
  const [actionBusy, setActionBusy] = useState<"cancel" | "retry" | "export" | null>(null);
  const [actionError, setActionError] = useState("");
  const [cancelKey, setCancelKey] = useState("");
  const [retryKey, setRetryKey] = useState("");

  useEffect(() => {
    if (!Number.isInteger(id) || id <= 0) {
      setError("任务编号无效");
      setLoading(false);
      return;
    }
    let disposed = false;
    let timer = 0;

    async function load() {
      try {
        const result = await api.tasks.detail(id);
        if (disposed) return;
        setTask(result);
        setError("");
        setUnsupported(false);
        if (taskIsActive(result)) timer = window.setTimeout(load, 3_000);
      } catch (reason) {
        if (disposed) return;
        if (isEndpointUnavailable(reason)) setUnsupported(true);
        else {
          setError(reason instanceof Error ? reason.message : "任务详情加载失败");
          timer = window.setTimeout(load, 5_000);
        }
      } finally {
        if (!disposed) setLoading(false);
      }
    }

    setLoading(true);
    void load();
    return () => {
      disposed = true;
      window.clearTimeout(timer);
    };
  }, [id, revision]);

  useEffect(() => {
    if (!task || taskIsActive(task)) return;
    setSelectedFailedIds(task.items.filter(isRetryableItem).map((item) => Number(item.item_id)));
    setRetryKey("");
  }, [task?.id, task?.status]);

  if (loading && !task) return <div className="page-stack"><LoadingRows count={6} /></div>;
  if (unsupported) return <EmptyState icon="tasks" title="任务详情接口不可用" description="当前环境尚不能从服务端恢复这个任务。" action={<Link className="button button--quiet" to="/tasks">返回任务列表</Link>} />;
  if (error && !task) return <ResourceError message={error} onRetry={() => setRevision((value) => value + 1)} />;
  if (!task) return null;

  const progress = taskProgress(task);
  const failedItems = task.items.filter((item) => item.status === "failed" || item.error_code || item.error_message);
  const retryableItems = task.items.filter(isRetryableItem);
  const errorGroups = failedItems.reduce<Record<string, number>>((groups, item) => {
    const key = item.error_code || item.error_message || "unknown_error";
    groups[key] = (groups[key] || 0) + 1;
    return groups;
  }, {});

  function toggleFailed(itemId: number) {
    setSelectedFailedIds((current) => current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId]);
    setRetryKey("");
    setActionError("");
  }

  async function performConfirmedAction() {
    if (!task) return;
    const currentTaskId = task.id;
    if (confirmAction === "cancel") {
      const key = cancelKey || createIdempotencyKey("task-cancel");
      if (!cancelKey) setCancelKey(key);
      setActionBusy("cancel");
      setActionError("");
      try {
        await api.tasks.cancel(currentTaskId, key);
        setConfirmAction(null);
        pushToast({ tone: "success", title: `已请求取消任务 #${currentTaskId}`, message: "运行中的账号会安全收尾，尚未开始的账号不会继续执行。" });
        feed.refresh();
        setRevision((value) => value + 1);
      } catch (reason) {
        setConfirmAction(null);
        setActionError(reason instanceof Error ? reason.message : "取消请求失败，请重试。");
      } finally {
        setActionBusy(null);
      }
      return;
    }
    if (confirmAction === "retry") {
      const selected = selectedFailedIds.filter((itemId) => retryableItems.some((item) => item.item_id === itemId));
      if (!selected.length) {
        setConfirmAction(null);
        setActionError("请至少选择一个可重试项再重试。");
        return;
      }
      const key = retryKey || createIdempotencyKey("task-retry");
      if (!retryKey) setRetryKey(key);
      setActionBusy("retry");
      setActionError("");
      try {
        const result = await api.tasks.retry(currentTaskId, selected, key);
        setConfirmAction(null);
        pushToast({ tone: "success", title: `重试任务 #${result.task_id} 已创建`, message: `服务端已接收 ${result.total} 个可重试项，不会重跑成功账号。` });
        feed.refresh();
        navigate(`/tasks/${result.task_id}`);
      } catch (reason) {
        setConfirmAction(null);
        setActionError(reason instanceof Error ? reason.message : "可重试项提交失败，请重试。");
      } finally {
        setActionBusy(null);
      }
    }
  }

  async function exportSuccess() {
    if (!task) return;
    const currentTaskId = task.id;
    const successCount = task.success_count;
    setActionBusy("export");
    setActionError("");
    try {
      const result = await api.tasks.exportResult(currentTaskId);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      pushToast({ tone: "success", title: "成功结果已导出", message: `${successCount} 条服务端结果已生成 ${result.filename}。` });
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : "结果导出失败，请重试。");
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <div className="page-stack task-detail-page">
      <div className="detail-breadcrumb"><Link to="/tasks">任务</Link><Icon name="arrow" size={15} /><span>#{task.id}</span></div>
      <section className="task-detail-hero">
        <div className="task-detail-hero__main">
          <div className="task-detail-hero__meta"><StatusPill status={taskIsDraining(task) ? "canceling" : task.status} /><span>并发 {task.concurrency || 1}</span><span>创建于 {formatDate(task.created_at)}</span>{task.source_task_id ? <Link to={`/tasks/${task.source_task_id}`}>重试自 #{task.source_task_id}</Link> : null}</div>
          <h2>任务 #{task.id} · {taskIsDraining(task) ? "取消处理中" : statusLabel(task.status)}</h2>
          <p>{taskIsDraining(task) ? "取消请求已接受，服务端正在安全收尾，页面会继续同步。" : taskIsActive(task) ? "服务端正在推进任务，页面会自动同步。" : `任务已结束，共处理 ${task.total_count} 个账号。`}</p>
          <div className="task-detail-actions">
            {["queued", "running"].includes(task.status) ? <button type="button" className="button button--danger-quiet" onClick={() => setConfirmAction("cancel")} disabled={Boolean(actionBusy)}>{actionBusy === "cancel" ? <span className="button-spinner" /> : null}取消任务</button> : null}
            {!taskIsActive(task) && retryableItems.length ? <button type="button" className="button button--quiet" onClick={() => setConfirmAction("retry")} disabled={Boolean(actionBusy) || !selectedFailedIds.length}><Icon name="refresh" size={17} />重试选中项 ({selectedFailedIds.length})</button> : null}
            {task.success_count > 0 ? <button type="button" className="button button--hero-quiet" onClick={() => void exportSuccess()} disabled={Boolean(actionBusy)}>{actionBusy === "export" ? <span className="button-spinner" /> : <Icon name="accounts" size={17} />}{actionBusy === "export" ? "生成文件中" : "导出成功结果"}</button> : null}
          </div>
        </div>
        <div className="task-detail-hero__progress"><b>{progress}</b><span>%</span><small>COMPLETE</small></div>
      </section>

      {error ? <ResourceError message={error} onRetry={() => setRevision((value) => value + 1)} /> : null}
      {actionError ? <div className="flow-error" role="alert">{actionError}</div> : null}

      <section className="task-stage-line" aria-label="任务阶段">
        <div className="is-done"><span><Icon name="check" size={17} /></span><b>已提交</b><small>{formatDate(task.created_at)}</small></div>
        <i className="is-done" />
        <div className={task.started_at ? "is-done" : "is-current"}><span><Icon name={task.started_at ? "check" : "clock"} size={17} /></span><b>授权执行</b><small>{formatDate(task.started_at)}</small></div>
        <i className={task.started_at ? "is-done" : ""} />
        <div className={task.finished_at ? "is-done" : taskIsActive(task) ? "is-current" : ""}><span><Icon name={task.finished_at ? "check" : "pulse"} size={17} /></span><b>结果落库</b><small>{formatDate(task.finished_at)}</small></div>
      </section>

      <section className="task-number-grid">
        <div><span>总账号</span><b>{task.total_count}</b><small>本次任务范围</small></div>
        <div className="is-success"><span>成功</span><b>{task.success_count}</b><small>授权已更新</small></div>
        <div className="is-danger"><span>失败</span><b>{task.failed_count}</b><small>可查看失败原因</small></div>
        <div className="is-warning"><span>扣分</span><b>{task.charged_count}</b><small>仅成功授权计费</small></div>
      </section>

      <div className="task-detail-grid">
        <section className="content-zone task-items-zone">
          <div className="section-heading"><div><span className="eyebrow">ITEM STATUS</span><h2>账号执行结果</h2></div><div className="section-heading__actions">{retryableItems.length ? <label className="check-control"><input type="checkbox" checked={selectedFailedIds.length === retryableItems.length} onChange={(event) => { setSelectedFailedIds(event.target.checked ? retryableItems.map((item) => Number(item.item_id)) : []); setRetryKey(""); }} /><span>选择全部可重试项</span></label> : null}<button className="icon-button" onClick={() => setRevision((value) => value + 1)} aria-label="刷新详情"><Icon name="refresh" size={19} /></button></div></div>
          {!task.items.length ? <EmptyState compact icon="clock" title="任务项尚未生成" description="任务仍在排队，稍后会自动出现。" /> : (
            <div className="task-item-table" role="table" aria-label="任务账号结果">
              <div className="task-item-table__head" role="row"><span>重试 / 序号</span><span>账号</span><span>阶段</span><span>状态</span><span>诊断</span></div>
              {task.items.map((item) => (
                <div className="task-item-table__row" role="row" key={item.item_id || item.line_no}>
                  <span data-label="重试 / 序号" className="task-item-selector">{isRetryableItem(item) ? <label><input type="checkbox" checked={selectedFailedIds.includes(item.item_id!)} onChange={() => toggleFailed(Number(item.item_id))} aria-label={`选择第 ${item.line_no} 行可重试项`} /><span>{item.line_no}</span></label> : item.line_no}</span>
                  <span data-label="账号">{item.account_id ? `#${item.account_id}` : "—"}</span>
                  <span data-label="阶段">{item.stage || "等待"}</span>
                  <span data-label="状态"><StatusPill status={item.status} /></span>
                  <span data-label="诊断">{item.error_message || item.sync_error || item.error_code || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="content-zone task-diagnostics">
          <div className="section-heading"><div><span className="eyebrow">DIAGNOSTICS</span><h2>失败聚合</h2></div></div>
          {!failedItems.length ? <EmptyState compact icon="check" title="没有失败诊断" description="当前任务项未报告错误。" /> : (
            <div className="error-groups">
              {Object.entries(errorGroups).slice(0, 6).map(([code, count]) => <div key={code}><span>{code}</span><b>{count}</b></div>)}
            </div>
          )}
          <details className="raw-log-drawer">
            <summary>查看原始任务日志</summary>
            <pre>{task.items.map((item) => [`line ${item.line_no}`, item.status, `stage=${item.stage || "-"}`, item.error_code ? `error=${item.error_code}` : "", item.error_message || ""].filter(Boolean).join(" | ")).join("\n") || "暂无日志"}</pre>
          </details>
        </aside>
      </div>
      <ConfirmDialog
        open={confirmAction === "cancel"}
        title={`取消任务 #${task.id}？`}
        description="尚未开始的账号会被取消；正在执行的账号需要完成安全收尾，状态可能短暂显示为取消处理中。"
        confirmLabel="确认取消"
        danger
        busy={actionBusy === "cancel"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => void performConfirmedAction()}
      />
      <ConfirmDialog
        open={confirmAction === "retry"}
        title={`重试 ${selectedFailedIds.length} 个可重试项？`}
        description="系统会创建一个新任务，只包含当前勾选的失败或积分不足跳过项；原任务和已成功结果保持不变。积分不足项请先充值，否则仍会被跳过。"
        confirmLabel="创建重试任务"
        busy={actionBusy === "retry"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => void performConfirmedAction()}
      />
    </div>
  );
}
